import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useCompanySettings } from './CompanySettingsContext.jsx';
import { useDateSettings } from './DateSettingsContext.jsx';
import { useStatblocksCache } from '../hooks/useStatblocksCache.js';
// Note: We no longer need to import statblock computation functions since they're handled by the backend

// Stable array for selectedStatblocks to prevent unnecessary re-renders
const DEFAULT_SELECTED_STATBLOCKS = ['sales', 'hourly', 'employees', 'tasks', 'samples', 'timers', 'orders', 'clockInVariance', 'scheduled', 'actual'];

// Create the context
const TimeSeriesDataContext = createContext();

// Provider component
export const TimeSeriesDataProvider = ({ children }) => {
  const { selectedStore, selectedStoreData } = useCompanySettings();
  const { startDate, endDate } = useDateSettings();
  
  // Use statblocks cache hook (replaces Zifusion data cache)
  const {
    statblocks,
    csvSections,
    weatherData,
    loading,
    error,
    cacheStatus,
    refreshCache,
    updateCsvSections,
    updateStatblocks,
    isCacheHit,
    isNetworkLoading,
    hasError
  } = useStatblocksCache(
    startDate ? startDate.format('YYYY-MM-DD') : null,
    endDate ? endDate.format('YYYY-MM-DD') : null,
    selectedStore !== 'no-store' ? selectedStore : null,
    selectedStoreData, // Pass store data for name and source_id
    DEFAULT_SELECTED_STATBLOCKS // Pass which statblocks to fetch
  );

  // Statblocks are now provided directly by the cache hook
  const statblocksLoading = loading;
  const statblocksError = error;

  // Parse CSV sections to include data arrays (kept for compatibility)
  const parsedCsvSections = useMemo(() => {
    return csvSections.map(section => {
      if (section.data) {
        // Already parsed
        return section;
      }
      
      if (!section.content) {
        return { ...section, data: [] };
      }

      try {
        // Simple CSV parsing - split by lines and then by commas
        const lines = section.content.trim().split('\n');
        if (lines.length === 0) {
          return { ...section, data: [] };
        }

        // First line is headers
        const headers = lines[0].split(',').map(h => h.trim());
        
        // Parse data rows
        const data = lines.slice(1).map(line => {
          const values = line.split(',').map(v => v.trim());
          const row = {};
          headers.forEach((header, index) => {
            row[header] = values[index] || '';
          });
          return row;
        });

        return { ...section, data, headers };
      } catch (error) {
        console.error(`Error parsing CSV section ${section.name}:`, error);
        return { ...section, data: [], error: error.message };
      }
    });
  }, [csvSections]);

  // Statblocks are now computed by the backend, so we don't need the computation logic
  // This is kept for compatibility but is essentially a no-op
  const computeStatblocks = useCallback(async () => {
    // Statblocks are already computed by the backend via the statblocks cache hook
    console.log('TimeSeriesData: Statblocks are now computed by the backend');
  }, []);

  // Memoize statblock types to prevent unnecessary re-renders
  const statblockTypes = useMemo(() => {
    return statblocks ? Object.keys(statblocks) : [];
  }, [statblocks]);

  // Debug logging for statblocks (only when statblock types change)
  useEffect(() => {
    if (statblockTypes.length > 0) {
      console.log('TimeSeriesData: Received statblocks from backend:', {
        dateRange: `${startDate?.format('YYYY-MM-DD')} to ${endDate?.format('YYYY-MM-DD')}`,
        store: selectedStore,
        statblockTypes,
        cacheStatus
      });
    }
  }, [statblockTypes, startDate, endDate, selectedStore, cacheStatus]);

  // Overall loading state (now just the statblocks loading since that includes everything)
  const overallLoading = loading;
  
  // Overall error state (now just the statblocks error since that includes everything)
  const overallError = error;

  // Memoize helper functions to prevent unnecessary re-renders
  const helperFunctions = useMemo(() => ({
    getSalesData: () => statblocks.sales,
    getHourlyData: () => statblocks.hourly,
    getEmployeeData: () => statblocks.employees,
    getTaskData: () => statblocks.tasks,
    getSampleData: () => statblocks.samples,
    getTimerData: () => statblocks.timers,
    getClockInVariance: () => statblocks.clockInVariance,
    getTimesheetData: () => statblocks.timesheets,
    getOrdersData: () => statblocks.orders,
  }), [statblocks]);

  // Context value (memoized to prevent unnecessary re-renders)
  const value = useMemo(() => ({
    // Raw CSV data (kept for compatibility, but now empty since we use statblocks directly)
    csvSections: parsedCsvSections,
    weatherData,
    
    // Computed statblocks (now from backend)
    statblocks,
    
    // Loading states
    loading: overallLoading,
    csvLoading: loading, // Map to the same loading state
    statblocksLoading: loading, // Map to the same loading state
    
    // Error states
    error: overallError,
    csvError: error, // Map to the same error state
    statblocksError: error, // Map to the same error state
    
    // Cache information
    cacheStatus,
    isCacheHit,
    isNetworkLoading,
    hasError,
    
    // Current selections (for debugging/display)
    startDate,
    endDate,
    selectedStore,
    
    // Utility functions
    refreshCache,
    updateCsvSections,
    updateStatblocks, // Add the new statblocks update function
    recomputeStatblocks: computeStatblocks, // Keep for compatibility
    
    // Helper functions for components
    ...helperFunctions,
    
    // Check if specific data is available
    hasSalesData: () => statblocks.sales && !statblocks.sales.error,
    hasHourlyData: () => statblocks.hourly && !statblocks.hourly.error,
    hasEmployeeData: () => statblocks.employees && !statblocks.employees.error,
    hasTaskData: () => statblocks.tasks && !statblocks.tasks.error,
    hasSampleData: () => statblocks.samples && !statblocks.samples.error,
    hasTimerData: () => statblocks.timers && !statblocks.timers.error,
  }), [
    parsedCsvSections,
    weatherData,
    statblocks,
    overallLoading,
    loading,
    overallError,
    error,
    cacheStatus,
    isCacheHit,
    isNetworkLoading,
    hasError,
    startDate,
    endDate,
    selectedStore,
    refreshCache,
    updateCsvSections,
    updateStatblocks,
    computeStatblocks,
    helperFunctions
  ]);

  return (
    <TimeSeriesDataContext.Provider value={value}>
      {children}
    </TimeSeriesDataContext.Provider>
  );
};

// Custom hook to use the context
export const useTimeSeriesData = () => {
  const context = useContext(TimeSeriesDataContext);
  if (!context) {
    throw new Error('useTimeSeriesData must be used within a TimeSeriesDataProvider');
  }
  return context;
};
